/// <amd-module name="Build/Designer/YamlFileService" />

import { CommitFileInfo, CommitFileRequestData, SavedFileInfo } from "Build/Client/Contracts/DataProvider";

import { RepositoryDescriptor } from "Build/Common/Library/Types";

import { RepositoryTypes } from "Build/SourceProviders/Common";

import { SaveResult } from "Build/Common/Library/Designer.types";
import * as Resources from "Build/Designer/Resources";

import { equals, format } from "VSS/Core/Util/String";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { DataProviderQuery, DataProviderResult } from "VSS/Platform/Contracts/Contributions";
import { IServerException, IVssContributionService } from "VSS/Platform/Contribution";
import { IContributionsRestClient } from "VSS/Platform/RestClient/Contributions";

export interface CommitDescriptor {
    message: string;
    description: string;
    createNewBranch: boolean;
    newBranchName: string;
    createPullRequest: boolean;
    skipCi?: boolean;
}

export interface FileContentInfo {
    content: string;
    branch: string;
    path: string;
    objectId: string;
    url: string;
}

/**
 * Commit the YAML file contents using the common data provider.
 */
export interface IYamlFileService extends IVssService {
    commitFile(repository: RepositoryDescriptor, branch: string, path: string, content: string, commitDescriptor: CommitDescriptor, oldObjectId?: string): Promise<SaveResult>;
    commitFiles(repository: RepositoryDescriptor, branch: string, files: CommitFileInfo[], commitDescriptor: CommitDescriptor): Promise<SaveResult>;
    getFileContentData(repository: RepositoryDescriptor, branch: string, path: string): Promise<FileContentInfo>;
}

class YamlFileService extends VssService implements IYamlFileService {
    public commitFile(repository: RepositoryDescriptor, branch: string, path: string, content: string, commitDescriptor: CommitDescriptor, oldObjectId?: string): Promise<SaveResult> {
        const files: CommitFileInfo[] = [{
            content: content,
            path: path,
            oldObjectId: oldObjectId || ""
        }];

        return this.commitFiles(repository, branch, files, commitDescriptor);
    }

    public commitFiles(repository: RepositoryDescriptor, branch: string, files: CommitFileInfo[], commitDescriptor: CommitDescriptor): Promise<SaveResult> {
        if (commitDescriptor.skipCi) {
            this._suppressCIBuild(commitDescriptor);
        }

        const dataProviderId = "ms.vss-build-web.commit-file-data-provider";
        const requestParams: CommitFileRequestData = {
            connectionId: repository.connectionId,
            sourceProviderType: repository.sourceProviderType,
            repositoryId: repository.id,
            projectId: repository.projectId || "",
            files: files,
            targetBranch: branch,
            sourceBranch: commitDescriptor.createNewBranch ? commitDescriptor.newBranchName : "",
            createPullRequest: commitDescriptor.createNewBranch && commitDescriptor.createPullRequest,
            message: commitDescriptor.message,
            description: commitDescriptor.description,
        };

        // If we need to specify a different Project scope for the data provider than our current page context,
        // then we must use the IContributionsRestClient directly.
        // This is currently required for committing to Azure Repos to avoid a CrossDataspaceAccessException.
        if (repository.projectId) {
            const query: DataProviderQuery = { contributionIds: [dataProviderId], context: { properties: requestParams } };
            query.context.properties.projectId = repository.projectId;
            const contributionsRestClient = this.pageContext.getRestClient<IContributionsRestClient>("IContributionsRestClient");
            return contributionsRestClient.queryDataProviders(query, "project", repository.projectId)
                .then((result: DataProviderResult) => {
                    if (result.resolvedProviders && result.resolvedProviders.length) {
                        if (result.exceptions && result.exceptions[dataProviderId]) {
                            throw new Error(result.exceptions[dataProviderId].message);
                        }
                        else {
                            const savedFileInfo = result.data[dataProviderId] || {} as SavedFileInfo;
                            const saveResult: SaveResult = { ...savedFileInfo, commitDescriptorName: commitDescriptor.message }
                            return saveResult;
                        }
                    }
                    else {
                        throw new Error(Resources.SaveErrorMessage);
                    }
                }, (error: Error) => {
                    throw new Error((error && error.message) || Resources.SaveErrorMessage);
                });
        }
        else {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            return contributionService.getDataAsync<SavedFileInfo, CommitFileRequestData>(dataProviderId, requestParams, true)
                .then((result) => {
                    if (result) {
                        const saveResult: SaveResult = { ...result, commitDescriptorName: commitDescriptor.message }
                        return saveResult;
                    }
                    else {
                        const ex: IServerException = contributionService.getDataProviderExceptions()[dataProviderId];
                        const message = (ex && ex.message) || Resources.SaveErrorMessage;
                        throw new Error(message);
                    }
                });
        }

    }

    public getFileContentData(repository: RepositoryDescriptor, branch: string, path: string): Promise<FileContentInfo> {
        const dataProviderId = "ms.vss-build-web.file-content-data-provider";
        const requestParams = {
            connectionId: repository.connectionId,
            sourceProvider: repository.sourceProviderType,
            repository: repository.id,
            path: path,
            branch: branch,
        };

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync<FileContentInfo>(dataProviderId, requestParams)
            .then((result: FileContentInfo) => {
                if (result) {
                    return result;
                }
                else {
                    const ex: IServerException = contributionService.getDataProviderExceptions()[dataProviderId];
                    let message = (ex && ex.message) || format(Resources.GetFileContentErrorMessage, path, branch);
                    message = this._processErrorMessage(message, repository);
                    throw new Error(message);
                }
            });
    }

    /** Add [skip ci] to the commit description to avoid triggering a CI build, unless it already contains a skip CI keyword */
    private _suppressCIBuild(commitDescriptor: CommitDescriptor) {
        let description = commitDescriptor.description || "";
        const fullMessage = (commitDescriptor.message || "") + "|" + description;
        const lowerCaseMessage = fullMessage.toLocaleLowerCase();

        if (skipCIKeywords.every(keyword => lowerCaseMessage.indexOf(keyword) === -1)) {
            description += (description ? " " : "") + skipCI;
            commitDescriptor.description = description;
        }
    }

    /** Replace the repository GUID with the friendly name (Azure repos) and any other message processing */
    private _processErrorMessage(message: string, repository: RepositoryDescriptor): string {
        let processedMessage = message || "";
        if (equals(repository.sourceProviderType, RepositoryTypes.TfsGit, true)
            && repository.name && repository.id) {
            processedMessage = message.replace(repository.id, repository.name);
        }
        return processedMessage;
    }
}

const skipCI = "[skip ci]";
const skipCIKeywords = [
    "***no_ci***",
    skipCI,
    "[ci skip]",
    "skip-checks: true",
    "skip-checks:true",
    "[skip azp]",
    "[azp skip]",
    "[skip azpipelines]",
    "[azpipelines skip]",
    "[skip azurepipelines]",
    "[azurepipelines skip]"
];

Services.add("IYamlFileService", { serviceFactory: YamlFileService });